import { useState } from "react";
import { Reveal } from "./Reveal";
import { BUSINESS, PRODUCTS } from "@/data/business";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, ArrowUpRight } from "lucide-react";

const ALL_PRODUCTS = PRODUCTS.flatMap((g) => g.items.map((i) => i.name));

const PURPOSES = [
  "Home Interior",
  "Office Interior",
  "Modular Furniture",
  "Construction / Shuttering",
  "Packaging / Crating",
  "Resale / Reseller",
  "Other",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  city: "",
  orderType: "wholesale",
  products: [],
  purpose: "",
  interiorHelp: "no",
  notes: "",
};

const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleProduct = (name) =>
    setForm((f) => ({
      ...f,
      products: f.products.includes(name)
        ? f.products.filter((p) => p !== name)
        : [...f.products, name],
    }));

  const buildMessage = () => {
    const lines = [
      `*New Enquiry — ${BUSINESS.name}*`,
      "",
      `*Name:* ${form.name}`,
      `*Phone:* ${form.phone}`,
      form.email ? `*Email:* ${form.email}` : null,
      form.city ? `*City:* ${form.city}` : null,
      `*Order Type:* ${form.orderType === "wholesale" ? "Wholesale" : "Retail"}`,
      `*Products of interest:* ${form.products.length ? form.products.join(", ") : "—"}`,
      form.purpose ? `*Purpose:* ${form.purpose}` : null,
      `*Interior design assistance needed:* ${form.interiorHelp === "yes" ? "Yes" : "No"}`,
      form.notes ? `*Additional notes:* ${form.notes}` : null,
      "",
      "Please share your best quote and availability. Thank you!",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    if (form.products.length === 0) {
      toast.error("Please select at least one product you're interested in.");
      return;
    }
    setSubmitting(true);
    const text = encodeURIComponent(buildMessage());
    const url = `https://wa.me/${BUSINESS.whatsappRaw}?text=${text}`;
    // Open synchronously inside the click handler to avoid Safari popup blocking.
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your enquiry…");
    setTimeout(() => setSubmitting(false), 400);
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="py-24 sm:py-32 bg-[color:var(--nitc-bg-2)]"
    >
      <div className="container-nitc">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="text-eyebrow">Get in touch</p>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--nitc-ink)] leading-[1.05] tracking-tight">
              Tell us what you need.<br />
              We'll WhatsApp you a quote.
            </h2>
            <p className="mt-6 text-[color:var(--nitc-text)] text-base sm:text-lg leading-relaxed">
              Fill in a few details and we'll draft a complete enquiry message for you. One tap and it
              lands on our WhatsApp — no calls, no spam.
            </p>

            <div className="mt-10 space-y-5 text-sm">
              <div className="flex gap-4 items-start">
                <span className="text-eyebrow mt-1">Call</span>
                <a href={`tel:${BUSINESS.phoneRaw}`} className="text-[color:var(--nitc-ink)] hover:text-[color:var(--nitc-accent)]">
                  {BUSINESS.phone}
                </a>
              </div>
              <div className="flex gap-4 items-start">
                <span className="text-eyebrow mt-1">Email</span>
                <a href={`mailto:${BUSINESS.email}`} className="text-[color:var(--nitc-ink)] hover:text-[color:var(--nitc-accent)]">
                  {BUSINESS.email}
                </a>
              </div>
              <div className="flex gap-4 items-start">
                <span className="text-eyebrow mt-1">Visit</span>
                <span className="text-[color:var(--nitc-text)]">{BUSINESS.address}</span>
              </div>
              <div className="flex gap-4 items-start">
                <span className="text-eyebrow mt-1">Hours</span>
                <span className="text-[color:var(--nitc-text)]">{BUSINESS.hours}</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="enquiry-form"
              className="bg-white rounded-lg border border-[color:var(--nitc-border)] p-6 sm:p-10 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_24px_60px_-32px_rgba(0,0,0,0.18)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                    Full name *
                  </Label>
                  <Input
                    id="name"
                    data-testid="enquiry-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    className="bg-[color:var(--nitc-bg-2)] border-transparent focus-visible:ring-[color:var(--nitc-accent)] focus-visible:ring-offset-0 h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="enquiry-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 ..."
                    className="bg-[color:var(--nitc-bg-2)] border-transparent focus-visible:ring-[color:var(--nitc-accent)] focus-visible:ring-offset-0 h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="enquiry-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    className="bg-[color:var(--nitc-bg-2)] border-transparent focus-visible:ring-[color:var(--nitc-accent)] focus-visible:ring-offset-0 h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                    City
                  </Label>
                  <Input
                    id="city"
                    data-testid="enquiry-city"
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    placeholder="Bangalore"
                    className="bg-[color:var(--nitc-bg-2)] border-transparent focus-visible:ring-[color:var(--nitc-accent)] focus-visible:ring-offset-0 h-12"
                  />
                </div>
              </div>

              <div className="mt-7 space-y-3">
                <Label className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                  Order type
                </Label>
                <RadioGroup
                  value={form.orderType}
                  onValueChange={(v) => update("orderType", v)}
                  className="grid grid-cols-2 gap-3"
                >
                  {[
                    { v: "wholesale", l: "Wholesale / Bulk" },
                    { v: "retail", l: "Retail / Personal" },
                  ].map((o) => (
                    <Label
                      key={o.v}
                      htmlFor={`ot-${o.v}`}
                      data-testid={`enquiry-ordertype-${o.v}`}
                      className={`cursor-pointer rounded-md border px-4 py-3.5 flex items-center gap-3 transition-all ${
                        form.orderType === o.v
                          ? "border-[color:var(--nitc-accent)] bg-[color:var(--nitc-accent)]/5"
                          : "border-[color:var(--nitc-border)] hover:border-[color:var(--nitc-ink)]/20"
                      }`}
                    >
                      <RadioGroupItem id={`ot-${o.v}`} value={o.v} className="border-[color:var(--nitc-accent)] text-[color:var(--nitc-accent)]" />
                      <span className="text-sm font-medium text-[color:var(--nitc-ink)]">{o.l}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>

              <div className="mt-7 space-y-3">
                <Label className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                  Products you're interested in *
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {ALL_PRODUCTS.map((p) => {
                    const checked = form.products.includes(p);
                    return (
                      <Label
                        key={p}
                        htmlFor={`p-${p}`}
                        data-testid={`enquiry-product-${p.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`cursor-pointer rounded-md border px-3 py-2.5 flex items-center gap-2.5 text-sm transition-all ${
                          checked
                            ? "border-[color:var(--nitc-accent)] bg-[color:var(--nitc-accent)]/5 text-[color:var(--nitc-ink)]"
                            : "border-[color:var(--nitc-border)] text-[color:var(--nitc-text)] hover:border-[color:var(--nitc-ink)]/20"
                        }`}
                      >
                        <Checkbox
                          id={`p-${p}`}
                          checked={checked}
                          onCheckedChange={() => toggleProduct(p)}
                          className="data-[state=checked]:bg-[color:var(--nitc-accent)] data-[state=checked]:border-[color:var(--nitc-accent)]"
                        />
                        {p}
                      </Label>
                    );
                  })}
                </div>
              </div>

              <div className="mt-7 grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                    Purpose / End use
                  </Label>
                  <Select value={form.purpose} onValueChange={(v) => update("purpose", v)}>
                    <SelectTrigger data-testid="enquiry-purpose" className="bg-[color:var(--nitc-bg-2)] border-transparent h-12 focus:ring-[color:var(--nitc-accent)] focus:ring-offset-0">
                      <SelectValue placeholder="Select a purpose" />
                    </SelectTrigger>
                    <SelectContent>
                      {PURPOSES.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                    Need interior design help?
                  </Label>
                  <RadioGroup
                    value={form.interiorHelp}
                    onValueChange={(v) => update("interiorHelp", v)}
                    className="flex gap-3"
                  >
                    {[
                      { v: "yes", l: "Yes" },
                      { v: "no", l: "No" },
                    ].map((o) => (
                      <Label
                        key={o.v}
                        htmlFor={`ih-${o.v}`}
                        data-testid={`enquiry-interior-${o.v}`}
                        className={`flex-1 cursor-pointer rounded-md border px-4 py-3 flex items-center gap-2 transition-all ${
                          form.interiorHelp === o.v
                            ? "border-[color:var(--nitc-accent)] bg-[color:var(--nitc-accent)]/5"
                            : "border-[color:var(--nitc-border)]"
                        }`}
                      >
                        <RadioGroupItem id={`ih-${o.v}`} value={o.v} className="border-[color:var(--nitc-accent)] text-[color:var(--nitc-accent)]" />
                        <span className="text-sm font-medium text-[color:var(--nitc-ink)]">{o.l}</span>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              <div className="mt-7 space-y-2">
                <Label htmlFor="notes" className="text-xs uppercase tracking-[0.18em] text-[color:var(--nitc-muted)]">
                  Additional notes
                </Label>
                <Textarea
                  id="notes"
                  data-testid="enquiry-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Sizes, quantities, deadlines, etc."
                  className="bg-[color:var(--nitc-bg-2)] border-transparent focus-visible:ring-[color:var(--nitc-accent)] focus-visible:ring-offset-0 resize-none"
                />
              </div>

              <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-button"
                  className="group inline-flex items-center justify-center gap-2 bg-[color:var(--nitc-accent)] hover:bg-[color:var(--nitc-accent-hover)] disabled:opacity-70 text-white text-sm font-medium px-7 py-4 rounded-md transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  {submitting ? "Opening WhatsApp…" : "Send Enquiry via WhatsApp"}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="text-xs text-[color:var(--nitc-muted)] leading-relaxed">
                  We'll draft your enquiry automatically and open WhatsApp on +91 82176 87406 — no
                  data is stored on this site.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;
